import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    IconButton,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControl,
    MenuItem,
    Select,
    Fade,
    Divider,
    useTheme,
    useMediaQuery,
    Tooltip,
    Chip,
} from "@mui/material";
import {
    Delete as DeleteIcon,
    Add as AddIcon,
    Save as SaveIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon
} from "@mui/icons-material";

import api from "../axios/axiosInstance";
import useSanitizationGuard from "../hooks/useSanitizationGuard";
import { SpinningGradelyLoader, FloatingGradelyLoader } from "../loadingIcons/LoadingIcons";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";


import {
    School as SchoolIcon,
    Book as BookIcon,
    Psychology as PsychologyIcon,
    Dashboard as DashboardIcon,
    LibraryBooks as BooksIcon
} from "@mui/icons-material";

const classLevelOptions = Array.from({ length: 8 }, (_, i) => i + 1);







const ManageSubjects = () => {
    const [subjects, setSubjects] = useState([]);
    const [originalSubjects, setOriginalSubjects] = useState([]);
    const [newSubjects, setNewSubjects] = useState([]);
    const [editStates, setEditStates] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, subject: null, stats: null });
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [validationDialog, setValidationDialog] = useState({ open: false, message: "" });
    const [duplicateDialog, setDuplicateDialog] = useState({ open: false, message: "" });
    const [saving, setSaving] = useState(false);
    const { check, WarningDialog } = useSanitizationGuard();

    const token = localStorage.getItem("access_token");

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const headers = {
        "Content-Type": "application/json"
    };

    const fetchSubjects = async () => {
        try {
            const res = await api.get(`/grades/subjects/`);
            const data = res.data;

            setSubjects(data);
            setOriginalSubjects(data);

            const editMap = {};
            data.forEach((s) => (editMap[s.id] = false));
            setEditStates(editMap);
        } catch (err) {
            // Error handled silently
        }
    };

    useEffect(() => {
        fetchSubjects();
    }, []);

    const handleAddRow = () => {
        setNewSubjects((prev) => [
            { id: Date.now(), subject_name: "", class_level: "", isNew: true, edit_mode: true },
            ...prev
        ]);
        setHasUnsavedChanges(true);
    };

    const handleChange = (id, field, value, isNew = false) => {
        const list = isNew ? [...newSubjects] : [...subjects];
        const idx = list.findIndex((s) => s.id === id);
        if (idx !== -1) {
            list[idx][field] = value;
            isNew ? setNewSubjects(list) : setSubjects(list);
            const hasChanges = checkForChanges(editStates, newSubjects, list, originalSubjects);
            setHasUnsavedChanges(hasChanges);
        }
    };
    const checkForChanges = (editMap, newList, currentSubjects, originals) => {
        const edited = currentSubjects.some((s) => {
            const original = originals.find((o) => o.id === s.id);
            return editMap[s.id] && original && (
                s.subject_name !== original.subject_name || s.class_level !== original.class_level
            );
        });
        const created = newList.some((s) => s.edit_mode && (s.subject_name || s.class_level));
        return edited || created;
    };

    const handleEditToggle = (id) => {
        const isChecked = !editStates[id];
        const updatedMap = { ...editStates, [id]: isChecked };
        if (!isChecked) {
            const original = originalSubjects.find((s) => s.id === id);
            const index = subjects.findIndex((s) => s.id === id);
            if (original && index !== -1) {
                const updatedSubjects = [...subjects];
                updatedSubjects[index] = { ...original };
                setSubjects(updatedSubjects);
            }
        }
        setEditStates(updatedMap);
        const hasChanges = checkForChanges(updatedMap, newSubjects, subjects, originalSubjects);
        setHasUnsavedChanges(hasChanges);
    };

    const handleEditAllToggle = (e) => {
        const checked = e.target.checked;
        const updatedMap = {};
        const updatedSubjects = [...subjects];

        subjects.forEach((s, idx) => {
            updatedMap[s.id] = checked;
            if (!checked) {
                const original = originalSubjects.find((o) => o.id === s.id);
                if (original) {
                    updatedSubjects[idx] = { ...original };
                }
            }
        });

        setSubjects(updatedSubjects);
        setEditStates(updatedMap);
        const hasChanges = checkForChanges(updatedMap, newSubjects, updatedSubjects, originalSubjects);
        setHasUnsavedChanges(hasChanges);
    };


    const handleSaveAll = async () => {
        const created = newSubjects.filter((s) => s.edit_mode);
        const updated = subjects.filter((s) => editStates[s.id]);

        for (let s of [...created, ...updated]) {
            if (!s.subject_name.trim() && !s.class_level) {
                setValidationDialog({ open: true, message: "❗ Both subject name and class level are required." });
                return;
            }
            if (!s.subject_name.trim()) {
                setValidationDialog({ open: true, message: "❗ Subject name is required." });
                return;
            }
            if (!s.class_level) {
                setValidationDialog({ open: true, message: "❗ Class level is required." });
                return;
            }
            if (check(s.subject_name, "Subject Name").blocked) return;
        }

        setSaving(true);
        try {
            for (let subject of created) {
                await api.post(`/grades/subjects/create/`, {
                    subject_name: subject.subject_name,
                    class_level: parseInt(subject.class_level)
                });
            }

            for (let subject of updated) {
                await api.patch(`/grades/subjects/${subject.id}/update/`, {
                    subject_name: subject.subject_name,
                    class_level: subject.class_level
                });
            }

            setSnackbar({ open: true, message: "✅ Changes saved successfully!", severity: "success" });
            setNewSubjects([]);
            setHasUnsavedChanges(false);
            fetchSubjects();
        } catch (err) {
            // Error handled silently

            // Check for specific duplicate subject error
            if (err.response?.status === 400) {
                const errorData = err.response.data;
                if (errorData?.subject_duplicate) {
                    setDuplicateDialog({ open: true, message: errorData.subject_duplicate });
                    return;
                } else if (errorData?.error && errorData.error.includes("already exists")) {
                    setDuplicateDialog({ open: true, message: errorData.error });
                    return;
                }
            }

            // For other errors, use snackbar
            setSnackbar({ open: true, message: "❌ Failed to save.", severity: "error" });
        } finally {
            setSaving(false);
        }
    };


    const handleDeleteClick = async (subject) => {
        const stats = {
            units: subject.units?.length || 0,
            progress_cards: Math.floor(Math.random() * 10),
        };
        setDeleteDialog({ open: true, subject, stats });
    };

    const confirmDelete = async () => {
        const { subject } = deleteDialog;
        try {
            await api.delete(`/grades/subjects/${subject.id}/delete/`);
            setSnackbar({ open: true, message: "🗑️ Subject deleted.", severity: "info" });
            fetchSubjects();
        } catch (err) {
            // Error handled silently
            setSnackbar({ open: true, message: "Delete failed.", severity: "error" });
        } finally {
            setDeleteDialog({ open: false, subject: null, stats: null });
        }
    };

    // cancel a brand‑new (unsaved) row
    const handleCancelNew = (id) => {
        setNewSubjects(prev => {
            const updated = prev.filter(s => s.id !== id);
            setHasUnsavedChanges(checkForChanges(editStates, updated, subjects, originalSubjects));
            return updated;
        });
    };

    // discard edits on an existing row
    const handleCancelEdit = (id) => {
        const original = originalSubjects.find(o => o.id === id);
        if (original) {
            setSubjects(prev => prev.map(s => (s.id === id ? { ...original } : s)));
        }
        setEditStates(prev => {
            const updated = { ...prev, [id]: false };
            setHasUnsavedChanges(checkForChanges(updated, newSubjects, subjects, originalSubjects));
            return updated;
        });
    };





    return (
        <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 1000, mx: "auto", backgroundColor: theme.palette.background.default, borderRadius: 3 }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    mb: 2,
                }}
            >
                <Box sx={{ width: 6, height: 36, bgcolor: "primary.main", borderRadius: 1 }} />
                <Typography variant="h5" fontWeight={700} color="text.primary" sx={{ fontSize: '1rem' }}>
                    Create a new Subject and assign its Class Level
                </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2.5, mb: 2, fontSize: '.9rem' }}>
                After creating a subject, you can add units in the <b>Units</b> tab.
            </Typography>


            <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3, overflowX: "auto" }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: "#708238" }}> {/* #f5f5f5*/}
                            <TableCell sx={{ color: "#fff", fontWeight: "bold" }} align="left"><Checkbox onChange={handleEditAllToggle} sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }} /> Edit</TableCell>
                            <TableCell sx={{ color: "#fff", fontWeight: "bold" }} align="left">Subject Name</TableCell>
                            <TableCell sx={{ color: "#fff", fontWeight: "bold" }} align="left">Class Level</TableCell>
                            <TableCell sx={{ color: "#fff", fontWeight: "bold" }} align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {[...newSubjects, ...subjects].map((subject) => {
                            const isNew = subject.isNew;
                            const isEditable = isNew ? subject.edit_mode : editStates[subject.id];
                            return (
                                <TableRow key={subject.id}>
                                    <TableCell>
                                        {!isNew && (
                                            <Checkbox
                                                checked={!!editStates[subject.id]}
                                                onChange={() => handleEditToggle(subject.id)}
                                            />
                                        )}
                                        {isNew && (
                                            <Chip label="New" color="primary" size="small" icon={<AddIcon />} />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            disabled={!isEditable}
                                            placeholder="Enter subject name"
                                            value={subject.subject_name}
                                            onChange={(e) => handleChange(subject.id, "subject_name", e.target.value, isNew)}
                                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', backgroundColor: 'white' } }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <FormControl fullWidth size="small">
                                            <Select
                                                disabled={!isEditable}
                                                value={subject.class_level}
                                                onChange={(e) => handleChange(subject.id, "class_level", e.target.value, isNew)}
                                                sx={{ borderRadius: '12px', backgroundColor: 'white' }}
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (!selected) return <em style={{ color: '#999' }}>Select Grade</em>;
                                                    return (
                                                        <Chip
                                                            label={`Grade ${selected}`}
                                                            size="small"
                                                            sx={{ backgroundColor: '#C8E6C9', color: '#333' }}
                                                        />
                                                    );
                                                }}
                                            >
                                                {classLevelOptions.map((level) => (
                                                    <MenuItem key={level} value={level}>
                                                        <Chip label={`Grade ${level}`} size="small" />
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell align="center">
                                        {isNew ? (
                                            // NEW ROW: show Cancel to remove it
                                            <Tooltip title="Cancel">
                                                <IconButton onClick={() => handleCancelNew(subject.id)}>
                                                    <CloseIcon />
                                                </IconButton>
                                            </Tooltip>
                                        ) : editStates[subject.id] ? (
                                            // EXISTING + in edit mode: allow discarding edits
                                            <Tooltip title="Discard changes">
                                                <IconButton onClick={() => handleCancelEdit(subject.id)}>
                                                    <UndoIcon />
                                                </IconButton>
                                            </Tooltip>
                                        ) : (
                                            // EXISTING + not editing: show Delete
                                            <Tooltip title="Delete Subject">
                                                <IconButton onClick={() => handleDeleteClick(subject)} sx={{ color: '#B3261E' }}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}

                        <TableRow>
                            <TableCell colSpan={4}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    gap: 2,
                                    mt: 3,
                                    alignItems: 'center'
                                }}>
                                    <Button variant="contained" onClick={handleAddRow} startIcon={<AddIcon />}>
                                        Add Subject
                                    </Button>
                                    <Button variant="contained" color="success" onClick={handleSaveAll} startIcon={<SaveIcon />}>
                                        Save Changes
                                    </Button>
                                    {hasUnsavedChanges && (
                                        <Fade in={true}>
                                            <Typography color="warning.main" fontWeight="bold">
                                                <WarningIcon fontSize="small" sx={{ mr: 1 }} />
                                                Unsaved changes
                                            </Typography>
                                        </Fade>
                                    )}
                                </Box>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    iconMapping={{
                        success: <CheckCircleIcon />,
                        error: <WarningIcon />,
                        info: <DeleteIcon />
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Validation Dialog */}
            <Dialog open={validationDialog.open} onClose={() => setValidationDialog({ open: false, message: "" })}>
                <DialogTitle>⚠️ Validation Error</DialogTitle>
                <DialogContent>
                    <Typography>{validationDialog.message}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setValidationDialog({ open: false, message: "" })}>OK</Button>
                </DialogActions>
            </Dialog>

            {/* Duplicate Subject Dialog */}
            <Dialog open={duplicateDialog.open} onClose={() => setDuplicateDialog({ open: false, message: "" })}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ color: '#B3261E' }} />
                    Subject Already Exists
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>
                        {duplicateDialog.message}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Please choose a different subject name or class level.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDuplicateDialog({ open: false, message: "" })}
                        variant="contained"
                        color="primary"
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, subject: null, stats: null })}
            >
                <DialogTitle>
                    <WarningIcon sx={{ mr: 1, color: "#B3261E" }} />
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete <strong>{deleteDialog.subject?.subject_name}</strong>? This will permanently remove:
                    </Typography>
                    <ul>
                        <li><strong>Any Units Created For This Subject</strong></li>
                        <li><strong>Any DOK Reports Generated For This Subject</strong></li>
                    </ul>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, subject: null, stats: null })}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" onClick={confirmDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <WarningDialog />

            {/* Loading State */}
            {saving && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        sx={{
                            p: { xs: 3, sm: 4 },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            backgroundColor: "white",
                            maxWidth: 400,
                            mx: 2,
                            boxShadow: 8
                        }}
                    >
                        <FloatingGradelyLoader
                            size={60}
                            showText={true}
                            text="Saving subject configurations to database..."
                        />
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ManageSubjects;
